import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState(null);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setEmailVerificationRequired(!response.data.email_verified);
        await fetchFeatures();
      } catch (error) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
      }
    }
    setLoading(false);
  };

  const fetchFeatures = async () => {
    try {
      const response = await axios.get(`${API}/auth/subscription-features`);
      setFeatures(response.data);
    } catch (error) {
      console.error('Error fetching features:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, email_verification_required } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    setEmailVerificationRequired(false);
    await fetchFeatures();
    return { user: userData, emailVerificationRequired: false };
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData, email_verification_required } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
    setEmailVerificationRequired(false);
    await fetchFeatures();
    return { user: userData, emailVerificationRequired: false };
  };

  const resendVerificationEmail = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email });
      return { success: true, message: response.data.message };
    } catch (error) {
      return { success: false, message: error.response?.data?.detail || 'Failed to send verification email' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setFeatures(null);
    setEmailVerificationRequired(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const value = {
    user,
    features,
    loading,
    login,
    register,
    logout,
    refreshUser,
    resendVerificationEmail,
    isAuthenticated: !!user,
    isEmailVerified: user?.email_verified || false,
    emailVerificationRequired,
    tier: user?.subscription_tier || 'free',
    token: localStorage.getItem('token'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
